import { catColors } from "@/domain/categories";
import type {
  Category,
  CategoryGroup,
  Layer,
  Layers,
  RapidFireMarks,
  RenderKeycap,
} from "@/domain/types";
import { readoutLegend } from "@/render/readout/use-readout";

/** One alternate's key on a list row: its code, its legend, and any rapid-fire mark. */
export interface ListRowKey {
  code: string;
  /** The key's legend through the active lens; search-by-key matches this. */
  legend: string;
  /** The key also carries a rapid-fire mark at this row's combo. */
  rapidFire?: boolean;
}

/**
 * One Command as a list row: its exact combo ("" = base), display fields, and one
 * key chip per alternate. Alternates of the same Command on the same combo merge
 * into a single row; the same Command on a different combo is a
 * separate row that sorts adjacent.
 */
export interface ListRow {
  /** Merge identity (the React key and hover match): the same string a row merged on. */
  id: string;
  combo: string;
  name: string;
  category: string;
  keys: ListRowKey[];
  /** A deferred command: it lost its cell to a survivor and is listed so the scan view shows it
   * (decision 0034). Only commands that are NOT a survivor anywhere in this view get a shadowed row -
   * a verb like Move that survives on its own key is already listed, so it adds no shadowed clutter. */
  shadowed?: boolean;
}

/** A Category group's section of the panel: header fields plus its filtered rows. */
export interface ListGroup extends CategoryGroup {
  accent: string;
  rows: ListRow[];
}

/** The fields a row merges on, as a collision-proof string: combo, name, category. */
function rowId(b: { name: string; category: string }, combo: string): string {
  return JSON.stringify([combo, b.name, b.category]);
}

/** Stable identity for a row across renders: the same string the row was merged on. */
export function rowKey(row: ListRow): string {
  return row.id;
}

/**
 * The panel's view of a Profile: every Binding across base and modifier layers,
 * merged per (name, category, combo) into one row with a chip per alternate
 * grouped by Category group and filtered by the search query (name
 * or any key legend, case-insensitive). Groups whose rows all filter away drop
 * out. Rows sort by Command name first, then base-before-modifier within a
 * Command, so a Command's alternates on different combos sit adjacent. A rapid-fire
 * mark flags the matching chip when marks is supplied.
 */
export function buildListGroups(
  layers: Layers,
  keycaps: RenderKeycap[],
  categories: Record<string, Category>,
  groups: CategoryGroup[],
  query: string,
  marks?: RapidFireMarks | null,
): ListGroup[] {
  const byId = new Map<string, ListRow>();
  const collect = (layer: Layer, combo: string) => {
    for (const [code, b] of Object.entries(layer)) {
      const id = rowId(b, combo);
      let row = byId.get(id);
      if (!row) {
        row = { id, combo, name: b.name, category: b.category, keys: [] };
        byId.set(id, row);
      }
      const key: ListRowKey = { code, legend: readoutLegend(code, keycaps) };
      if (marks?.[combo]?.has(code)) key.rapidFire = true;
      row.keys.push(key);
    }
  };
  collect(layers.base, "");
  for (const [combo, layer] of Object.entries(layers.mods ?? {})) {
    collect(layer, combo);
  }

  // shadowed (deferred) commands: list each one that is NOT already a survivor somewhere in this view,
  // so a genuinely-hidden command (Block folded onto Melee, Stasis Breakout onto Class) still appears
  // in the scan and is search-findable - while a verb that survives on its own key adds no clutter.
  const survivorKeys = new Set([...byId.values()].map((r) => `${r.name}|${r.category}`));
  const collectShadows = (layer: Layer, combo: string) => {
    for (const [code, b] of Object.entries(layer)) {
      for (const s of b.shadowed ?? []) {
        if (survivorKeys.has(`${s.name}|${s.category}`)) continue;
        const id = `${rowId(s, combo)}|shadowed`;
        let row = byId.get(id);
        if (!row) {
          row = { id, combo, name: s.name, category: s.category, keys: [], shadowed: true };
          byId.set(id, row);
        }
        row.keys.push({ code, legend: readoutLegend(code, keycaps) });
      }
    }
  };
  collectShadows(layers.base, "");
  for (const [combo, layer] of Object.entries(layers.mods ?? {})) {
    collectShadows(layer, combo);
  }

  // Map preserves insertion order, so values() yields rows in first-seen order (the old `order`
  // array and its non-null cast were redundant).
  const rows = [...byId.values()];
  for (const row of rows) row.keys.sort((a, b) => a.legend.localeCompare(b.legend));

  const q = query.trim().toLowerCase();
  return groups
    .map((g) => {
      let gr = rows.filter((r) => g.members.includes(r.category));
      if (q) {
        gr = gr.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            r.keys.some((k) => k.legend.toLowerCase().includes(q)),
        );
      }
      // command name first, then base-before-modifier within a command
      gr = [...gr].sort(
        (a, b) =>
          a.name.localeCompare(b.name) ||
          (a.combo ? 1 : 0) - (b.combo ? 1 : 0) ||
          a.combo.localeCompare(b.combo),
      );
      const first = g.members[0];
      return { ...g, accent: catColors(first ? categories[first] : null).accent, rows: gr };
    })
    .filter((g) => g.rows.length > 0);
}
